import {BaseBadge, BaseCard, BasePagination, BaseSelect, BaseTable} from "@/components";
import type {Column} from "@/components/BaseTable/BaseTable";
import {useCommissionLedger, useCommissionLedgerSummary} from "@/hooks/useCommissionLedger";
import type {CommissionLedgerEntry} from "@/services/commission-ledger.service";
import {DollarSign, Hash, TrendingUp} from "lucide-react";
import {useState} from "react";

const operationTypeLabel: Record<string, string> = {
  pay_in: "Pay-In",
  pay_out: "Pay-Out",
  currency_conversion: "Conversión",
  remittance: "Remesa",
  p2p_transfer: "P2P",
};

const operationTypeBadge: Record<string, "info" | "warning" | "success" | "neutral"> = {
  pay_in: "success",
  pay_out: "warning",
  currency_conversion: "info",
  remittance: "info",
  p2p_transfer: "neutral",
};

const operationTypeOptions = [
  {value: "all", label: "Todas las operaciones"},
  {value: "pay_in", label: "Pay-In"},
  {value: "pay_out", label: "Pay-Out"},
  {value: "currency_conversion", label: "Conversión"},
  {value: "remittance", label: "Remesa"},
  {value: "p2p_transfer", label: "P2P"},
];

const currencyOptions = [
  {value: "all", label: "Todas las monedas"},
  {value: "USD", label: "USD"},
  {value: "CLP", label: "CLP"},
  {value: "COP", label: "COP"},
  {value: "PEN", label: "PEN"},
  {value: "MXN", label: "MXN"},
  {value: "EUR", label: "EUR"},
];

const formatCents = (cents: number, currency: string): string => {
  const value = cents / 100;
  try {
    return new Intl.NumberFormat("es-CL", {style: "currency", currency, maximumFractionDigits: 2}).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
};

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {dateStyle: "short", timeStyle: "short"}).format(new Date(iso));
};

export function CommissionLedger() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [operationFilter, setOperationFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");

  const offset = (page - 1) * limit;
  const filters = {
    limit,
    offset,
    ...(operationFilter !== "all" ? {operation_type: operationFilter} : {}),
    ...(currencyFilter !== "all" ? {currency: currencyFilter} : {}),
  };

  const {data, isLoading} = useCommissionLedger(filters);
  const {data: summary} = useCommissionLedgerSummary(currencyFilter !== "all" ? currencyFilter : undefined);

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {total: 0, page: 1, limit: 20, totalPages: 1};

  const columns: Column<CommissionLedgerEntry>[] = [
    {
      key: "operation_type",
      header: "Operación",
      render: row => (
        <BaseBadge
          variant={operationTypeBadge[row.operation_type] ?? "neutral"}
          label={operationTypeLabel[row.operation_type] ?? row.operation_type}
        />
      ),
    },
    {
      key: "user_id",
      header: "Usuario",
      render: row => (
        <span className="text-xs font-mono text-(--color-text-secondary) max-w-32 truncate block">{row.user_id}</span>
      ),
    },
    {
      key: "amount_cents",
      header: "Comisión",
      render: row => (
        <span className="text-sm font-semibold font-mono tabular-nums text-(--color-text-primary)">
          {formatCents(row.amount_cents, row.currency)}
        </span>
      ),
    },
    {
      key: "currency",
      header: "Moneda",
      render: row => <span className="text-sm text-(--color-text-secondary)">{row.currency}</span>,
    },
    {
      key: "description",
      header: "Descripción",
      render: row => (
        <span className="text-xs text-(--color-text-tertiary) max-w-60 truncate block">{row.description}</span>
      ),
    },
    {
      key: "created_at",
      header: "Fecha",
      render: row => <span className="text-xs text-(--color-text-tertiary)">{formatDate(row.created_at)}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-(--color-text-primary)">Commission Ledger</h1>
        <p className="text-sm text-(--color-text-secondary) mt-1">Historial de comisiones cobradas — Solo lectura</p>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCard
            icon={<DollarSign size={20} />}
            label="Total comisiones"
            value={formatCents(summary.total_cents, summary.currency)}
          />
          <SummaryCard
            icon={<Hash size={20} />}
            label="Operaciones"
            value={summary.count.toLocaleString("es-CL")}
          />
          <SummaryCard
            icon={<TrendingUp size={20} />}
            label="Por tipo"
            value={`${summary.by_operation_type.length} tipos`}
          />
        </div>
      )}

      {/* Breakdown by operation type */}
      {summary && summary.by_operation_type.length > 0 && (
        <BaseCard padding="md">
          <p className="text-sm font-medium text-(--color-text-primary) mb-3">Desglose por operación</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {summary.by_operation_type.map(item => (
              <div
                key={item.operation_type}
                className="p-3 rounded-md bg-(--color-surface-2)"
              >
                <p className="text-xs text-(--color-text-tertiary)">
                  {operationTypeLabel[item.operation_type] ?? item.operation_type}
                </p>
                <p className="text-sm font-semibold font-mono tabular-nums text-(--color-text-primary) mt-1">
                  {formatCents(item.total_cents, summary.currency)}
                </p>
                <p className="text-xs text-(--color-text-tertiary)">{item.count} ops</p>
              </div>
            ))}
          </div>
        </BaseCard>
      )}

      {/* Filters */}
      <BaseCard padding="md">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-45">
            <BaseSelect
              label="Operación"
              options={operationTypeOptions}
              value={operationFilter}
              onChange={e => {
                setOperationFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="min-w-40">
            <BaseSelect
              label="Moneda"
              options={currencyOptions}
              value={currencyFilter}
              onChange={e => {
                setCurrencyFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
      </BaseCard>

      {/* Table */}
      <BaseCard padding="none">
        <BaseTable<CommissionLedgerEntry>
          data={items}
          columns={columns}
          loading={isLoading}
          emptyMessage="No hay entradas en el ledger."
        />
        {pagination.totalPages > 1 && (
          <BasePagination
            page={page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </BaseCard>
    </div>
  );
}

function SummaryCard({icon, label, value}: {icon: React.ReactNode; label: string; value: string}) {
  return (
    <BaseCard padding="md">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-(--color-surface-2) text-(--color-text-secondary)">{icon}</div>
        <div>
          <p className="text-xs text-(--color-text-tertiary)">{label}</p>
          <p className="text-lg font-bold font-mono tabular-nums text-(--color-text-primary)">{value}</p>
        </div>
      </div>
    </BaseCard>
  );
}
